// FP 간트 — 작업 검색(pill + Popover)
//
// old `wingui-core/component/gantt/ActivitySearch.jsx` 대응. 접힘 pill(라벨 = `주문 / 품목`) +
// Popover(2필드 + 검색 버튼) + X 초기화 + Enter 검색까지 old와 같은 조작이다.
// 시안·설계 근거: reports/gantt-implementation.md §7-8, prototypes/gantt-activity-search.html
//
// **왜 조회조건 줄이 아니라 간트 툴바에 두는가**: 이 검색은 서버로 가지 않는 **화면 안의 강조 필터**다
// (커밋해도 재조회 없이 캔버스만 다시 그린다). 조회조건 줄에 두면 "바꾸면 재조회된다"로 읽히고, 실제로
// 옆의 자원 멀티셀렉트·조회 구간은 재조회된다. 버킷을 표시 버튼으로 옮긴 것과 같은 논리이며(§7-5)
// old도 이 검색만은 간트 툴바의 pill로 따로 뒀다.
// ⚠️ **주문 간트에는 쓰지 않는다** — 그쪽 주문/품목 입력은 서버 파라미터로 나가는 진짜 조회조건이다.
//
// old에 없던 것 두 가지만 더한다(둘 다 "흐려진 화면에서 결과를 찾는" 문제를 푼다):
//   ① 건수 배지 — `현재/전체` 형태(브라우저 찾기와 같은 정신 모델). 숫자만이라 번역이 필요 없다.
//   ② ⌃⌄ 이동 — 일치 작업 사이 점프. 0건이면 아예 나타나지 않는다.

import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";

import { LATE_TEXT_BG, LATE_TEXT_COLOR, UI } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

const EMPTY = { order: "", item: "" };

/**
 * props
 *  - value: { order, item }                  커밋된 검색값(pill 라벨·배지 표시용, 소유권은 화면)
 *  - onCommit: ({ order, item }) => void      검색·초기화 모두 이 하나로 커밋한다
 *  - fields: [{ key, labelKey }]              팝오버 입력 목록(화면의 SEARCH_FIELD_LABELS와 같은 출처)
 *  - matchCount / matchIndex                  일치 건수 / 현재 위치(0-base). 계산은 화면이 한다
 *  - onStep: (delta) => void                  ⌃⌄ — 화면이 스크롤·선택까지 처리
 */
const GanttActivitySearch = ({ t, value = EMPTY, onCommit, fields = [], matchCount = 0, matchIndex = 0, onStep }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  // 팝오버 안에서 편집 중인 값. **커밋 전에는 화면 상태를 건드리지 않는다** — 키 입력마다 캔버스를
  // 다시 그리면 검색어를 타이핑하는 동안 화면이 계속 흐려졌다 밝아진다.
  const [draft, setDraft] = useState(value);

  const open = Boolean(anchorEl);
  const hasValue = Boolean(value.order || value.item);

  // 밖에서 검색값이 바뀌면(초기화 등) 편집값도 따라간다.
  useEffect(() => { setDraft(value); }, [value]);

  const openPopover = (event) => {
    setDraft(value);
    setAnchorEl(event.currentTarget);
  };

  const commit = () => {
    onCommit({ order: (draft.order ?? "").trim(), item: (draft.item ?? "").trim() });
    setAnchorEl(null);
  };

  const clear = (event) => {
    // pill 자체의 onClick(팝오버 열기)으로 번지면 지운 직후 팝오버가 열린다.
    event.stopPropagation();
    onCommit(EMPTY);
    setAnchorEl(null);
  };

  const step = (event, delta) => {
    event.stopPropagation();
    onStep?.(delta);
  };

  return (
    <>
      <Box
        onClick={openPopover}
        sx={{
          display: "flex", alignItems: "center", gap: 0.75, height: 30, minWidth: 240, px: 1.25,
          border: "1px solid", borderColor: open ? "primary.main" : "#ced4da", borderRadius: 999,
          bgcolor: "background.paper", cursor: "pointer", userSelect: "none",
          ...(open ? { boxShadow: `0 0 0 2px ${UI.headerAccent}26` } : null),
          "&:hover": { borderColor: open ? "primary.main" : "#adb5bd" }
        }}
      >
        <SearchIcon sx={{ fontSize: 16, color: "#8996a3", flex: "none" }} />
        <Typography
          sx={{
            flex: "1 1 auto", fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis", color: hasValue ? "text.primary" : "text.secondary"
          }}
        >
          {hasValue ? `${value.order || ""} / ${value.item || ""}` : t("FP_ACTIVITY_SEARCH")}
        </Typography>

        {hasValue ? (
          // 배지 = `현재/전체`. 0건은 붉은 계열로 갈라 "못 찾았다"를 명시한다 — 흐려진 화면만 보고
          // "고장났다"고 읽는 것을 막는 유일한 신호다.
          <Typography
            sx={{
              flex: "none", fontSize: 11, fontWeight: 700, borderRadius: 999, px: 0.875,
              fontVariantNumeric: "tabular-nums",
              color: matchCount === 0 ? LATE_TEXT_COLOR : "primary.main",
              bgcolor: matchCount === 0 ? LATE_TEXT_BG : UI.tipBandBg
            }}
          >
            {matchCount === 0 ? "0" : `${Math.min(matchIndex + 1, matchCount)}/${matchCount}`}
          </Typography>
        ) : null}

        {matchCount > 0 ? (
          <Box sx={{ flex: "none", display: "flex" }}>
            <Tooltip title={t("prev")}>
              <IconButton size="small" sx={{ p: 0.125 }} onClick={(event) => step(event, -1)}>
                <KeyboardArrowUpIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("next")}>
              <IconButton size="small" sx={{ p: 0.125 }} onClick={(event) => step(event, 1)}>
                <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        {hasValue ? (
          <Tooltip title={t("reset")}>
            <IconButton size="small" sx={{ p: 0.125, flex: "none" }} onClick={clear}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.75, borderRadius: 2 } } }}
      >
        <Paper elevation={0} sx={{ width: 260, p: 2 }}>
          {/* 입력은 **시스템 표준(outlined + floating label)**으로 둔다 — 조회조건 줄의 다른 컨트롤
              (자원 Autocomplete·버킷 Select)과 같은 부품이라 팝오버만 다른 부품을 쓰면 겉돈다.
              밑줄(standard)로 내리면 더 가벼워지지만 "이 앱의 입력은 outlined"라는 규칙에 예외가
              생기고, 이 팝오버가 무거웠던 원인은 입력 스타일이 아니라 여백과 불필요한 버튼이었다.
              `pt`는 떠오른 라벨이 카드 상단에 잘리지 않게 하는 여백이다. */}
          <Box sx={{ display: "grid", gap: 1.5, pt: 0.75 }}>
            {fields.map(field => (
              <TextField
                key={field.key}
                size="small"
                fullWidth
                label={t(field.labelKey)}
                autoFocus={field === fields[0]}
                value={draft[field.key] ?? ""}
                onChange={(event) => setDraft(prev => ({ ...prev, [field.key]: event.target.value }))}
                onKeyDown={(event) => { if (event.key === "Enter") commit(); }}
              />
            ))}
          </Box>

          {/* **취소 버튼을 두지 않는다**(2026-08-21 재검토): `Popover`의 `onClose`가 바깥 클릭·Esc에서
              기본으로 발동하고, 열려 있는 동안 backdrop이 pill을 덮어 pill 재클릭으로도 닫힌다 →
              나가는 길이 이미 3개인데 버튼이 **완전히 같은 함수**(`setAnchorEl(null)`)를 부르는 중복이었다.
              취소가 값어치를 갖는 곳은 화면을 막고 비가역 동작을 하는 **모달**이다(`AdjustQtyPopup`은 Dialog).
              여기서는 커밋 전까지 화면 상태가 전혀 바뀌지 않으므로 되돌릴 것도 없다.
              커밋 경로는 Enter와 이 버튼 둘 — Enter를 모르는 사용자를 위해 버튼은 남긴다. */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.75 }}>
            <Button size="small" variant="contained" onClick={commit}>{t("search")}</Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default GanttActivitySearch;
